import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GoogleButton from 'react-google-button';
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { useAuth } from '../firebase/AuthContext';
import { auth, db, signInWithGoogle } from '../firebase/firebaseConfig';
import AlertModal from '../components/payments/server/alertModal';
import '../styles/pages/signinpage.css';

const LOGIN_SERVICE = 'ui.login';
const LOGIN_REQUESTER = { entityId: 'auth.login' };
const FORBIDDEN_SEQUENCES = [
  '1234567890',
  '123456789!',
  '123456789A',
  '123456789a',
  'Qwertyuiop1!',
  'Password123!',
  'Password1!',
  '1231231231',
  '1Q2w3E4r5T!',
  '1q2w3e4r5t!',
  '12345678A1!',
  'Abc1234567!',
  '12345Qwert!',
  'Qwerty1234!',
  '1111111111!',
  'Asdfghjkl1!',
  'Zxcvbnm123!',
  'Passw0rd1!',
  'QazwsxedcR1!',
  'Letmein123!'
];

function SignInPage({ googleSignInDebounceMilliseconds = 750 }) {
  const [tab, setTab] = useState('login');
  const [resetMode, setResetMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signUpForm, setSignUpForm] = useState({
    email: '',
    password: '',
    confirm: ''
  });
  const [resetEmail, setResetEmail] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [isGoogleSignInInProgress, setIsGoogleSignInInProgress] =
    useState(false);
  const [alertModalState, setAlertModalState] = useState({
    open: false,
    preset: null,
    variant: 'info'
  });

  const googlePopupInFlightRef = useRef(false);
  const googleLastClickTimestampRef = useRef(0);

  const { signup } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isGoogleButtonDisabled = isGoogleSignInInProgress || loading;
  const requestedLocation = location.state?.from;
  const requestedPathname = requestedLocation?.pathname;
  const postAuthenticationDestination =
    typeof requestedPathname === 'string' &&
    requestedPathname.startsWith('/') &&
    !requestedPathname.startsWith('//') &&
    !requestedPathname.includes('\\') &&
    requestedPathname !== '/sign-in' &&
    requestedPathname !== '/signin'
      ? {
          pathname: requestedPathname,
          search:
            typeof requestedLocation.search === 'string' &&
            requestedLocation.search.startsWith('?')
              ? requestedLocation.search
              : '',
          hash:
            typeof requestedLocation.hash === 'string' &&
            requestedLocation.hash.startsWith('#')
              ? requestedLocation.hash
              : ''
        }
      : '/home';
  const postAuthenticationState =
    typeof postAuthenticationDestination === 'object'
      ? requestedLocation.state
      : undefined;

  const showAlertModal = (presetName, variant = 'error') => {
    setAlertModalState({
      open: true,
      preset: presetName,
      variant
    });
  };

  const closeAlertModal = () => {
    setAlertModalState(previousState => ({
      ...previousState,
      open: false
    }));
  };

  const handleChange = (event, setFormState) => {
    const { name, value } = event.target;
    setFormState(previousState => ({
      ...previousState,
      [name]: value
    }));
  };

  const clearMessaging = () => {
    setError('');
    setInfo('');
  };

  const switchTab = newTab => {
    setTab(newTab);
    setResetMode(false);
    clearMessaging();
  };

  const enterResetMode = () => {
    setResetMode(true);
    clearMessaging();
  };

  const exitResetMode = () => {
    setResetMode(false);
    clearMessaging();
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(previousValue => !previousValue);
  };

  const ensureUserDocument = async (userId, accountCreatedSource) => {
    const userDocumentReference = doc(db, 'users', userId);
    const userSnapshot = await getDoc(userDocumentReference);

    if (!userSnapshot.exists()) {
      await setDoc(userDocumentReference, {
        accountCreatedAt: serverTimestamp(),
        accountCreatedSource
      });
    }
  };

  const handleLoginSubmit = async event => {
    event.preventDefault();
    clearMessaging();
    setLoading(true);

    const { email, password } = loginForm;

    try {
      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );

      const credentials = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      await ensureUserDocument(credentials.user.uid, 'password');
      navigate(postAuthenticationDestination, {
        replace: true,
        state: postAuthenticationState
      });
    } catch (submissionError) {
      if (submissionError.code === 'auth/user-not-found') {
        setError('No account found with this email. Please Sign Up.');
      } else if (submissionError.code === 'auth/wrong-password') {
        setError('Email or password is incorrect.');
      } else if (submissionError.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (submissionError.code === 'auth/network-request-failed') {
        showAlertModal('auth_generic_network_error');
      } else {
        setError('Email or password is incorrect.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const currentTimestamp = Date.now();
    const lastClickTimestamp = googleLastClickTimestampRef.current;

    if (
      googleSignInDebounceMilliseconds > 0 &&
      currentTimestamp - lastClickTimestamp <
        googleSignInDebounceMilliseconds
    ) {
      return;
    }

    googleLastClickTimestampRef.current = currentTimestamp;

    if (googlePopupInFlightRef.current) {
      return;
    }

    googlePopupInFlightRef.current = true;
    setIsGoogleSignInInProgress(true);
    clearMessaging();
    setLoading(true);

    try {
      const credentials = await signInWithGoogle();

      await ensureUserDocument(credentials.user.uid, 'google');
      navigate(postAuthenticationDestination, {
        replace: true,
        state: postAuthenticationState
      });
    } catch (submissionError) {
      let presetName = 'auth_google_generic_error';

      switch (submissionError.code) {
        case 'auth/popup-closed-by-user':
          presetName = 'auth_google_popup_closed';
          break;
        case 'auth/cancelled-popup-request':
          presetName = 'auth_google_cancelled';
          break;
        case 'auth/account-exists-with-different-credential':
          presetName = 'auth_google_account_exists';
          break;
        case 'auth/network-request-failed':
          presetName = 'auth_google_network_error';
          break;
        default:
          presetName = 'auth_google_generic_error';
      }

      showAlertModal(presetName, 'error');
    } finally {
      googlePopupInFlightRef.current = false;
      setIsGoogleSignInInProgress(false);
      setLoading(false);
    }
  };

  const handleSignUpSubmit = async event => {
    event.preventDefault();
    clearMessaging();
    setLoading(true);

    const { email, password, confirm } = signUpForm;

    try {
      const domain = email.split('@')[1]?.toLowerCase() || '';

      try {
        const response = await fetch(
          `https://dns.google/resolve?name=${domain}&type=MX`
        );
        const jsonResponse = await response.json();

        if (
          !Array.isArray(jsonResponse.Answer) ||
          jsonResponse.Answer.length === 0
        ) {
          setError(
            'Email domain not found. Please use a valid email provider.'
          );
          return;
        }
      } catch {
        setError('Unable to verify email domain. Try again later.');
        return;
      }

      if (password !== confirm) {
        setError('Passwords do not match');
        return;
      }
      if (password.length < 10) {
        setError('Password must be at least 10 characters long');
        return;
      }
      if (!/[A-Z]/.test(password)) {
        setError('Must contain at least one uppercase letter');
        return;
      }
      if (!/[a-z]/.test(password)) {
        setError('Must contain at least one lowercase letter');
        return;
      }
      if (!/[0-9]/.test(password)) {
        setError('Must contain at least one number');
        return;
      }
      if (!/[^A-Za-z0-9]/.test(password)) {
        setError('Must contain at least one special character');
        return;
      }
      if (password.toLowerCase().includes(email.toLowerCase())) {
        setError('Must not contain your email address');
        return;
      }

      const username = email.split('@')[0];
      if (password.toLowerCase().includes(username.toLowerCase())) {
        setError('Must not contain your username');
        return;
      }

      if (
        FORBIDDEN_SEQUENCES.find(sequence =>
          password.toLowerCase().includes(sequence.toLowerCase())
        )
      ) {
        setError('Password contains a common/forbidden sequence');
        return;
      }

      const credentials = await signup(email, password);
      await setDoc(doc(db, 'users', credentials.user.uid), {
        accountCreatedAt: serverTimestamp(),
        accountCreatedSource: 'signup'
      });

      navigate(postAuthenticationDestination, {
        replace: true,
        state: postAuthenticationState
      });
    } catch (submissionError) {
      if (submissionError.code === 'auth/email-already-in-use') {
        setError(
          'An account already exists. Log in or reset your password if needed.'
        );
      } else if (submissionError.code === 'auth/network-request-failed') {
        showAlertModal('auth_generic_network_error');
      } else {
        setError('Signup failed: ' + submissionError.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async event => {
    event.preventDefault();
    clearMessaging();
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, resetEmail.trim());
      setInfo('Check your inbox for a reset link.');
    } catch (submissionError) {
      if (submissionError.code === 'auth/user-not-found') {
        setError('No account found with this email.');
      } else if (submissionError.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (submissionError.code === 'auth/network-request-failed') {
        showAlertModal('auth_generic_network_error');
      } else {
        setError('Failed to send reset email. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="signin-page">
        <div className="signin-page__container">
          <div className="signin-page__header">
            <div className="signin-page__brand">
              <svg
                className="signin-page__brand-icon"
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
              <h1 className="signin-page__brand-title">Pure Health Nexus</h1>
            </div>
            <p className="signin-page__portal-subtitle">15 Day Free Trial</p>
          </div>

          <div className="signin-page__card">
            <div className="signin-page__tabs">
              {!resetMode ? (
                <>
                  <button
                    type="button"
                    className={`signin-page__tab-button ${
                      tab === 'login' ? 'signin-page__tab-button--active' : ''
                    }`}
                    onClick={() => switchTab('login')}
                  >
                    Log In
                  </button>
                  <button
                    type="button"
                    className={`signin-page__tab-button ${
                      tab === 'signup' ? 'signin-page__tab-button--active' : ''
                    }`}
                    onClick={() => switchTab('signup')}
                  >
                    Create Account
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="signin-page__tab-button signin-page__tab-button--active"
                >
                  Password Reset
                </button>
              )}
            </div>

            {resetMode ? (
              <form onSubmit={handleResetSubmit}>
                {error && (
                  <div className="signin-page__alert signin-page__alert--danger">
                    {error}
                  </div>
                )}
                {info && (
                  <div className="signin-page__alert signin-page__alert--info">
                    {info}
                  </div>
                )}

                <div className="signin-page__input-group">
                  <label
                    htmlFor="reset-email"
                    className="signin-page__input-label"
                  >
                    Work Email Address
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    name="resetEmail"
                    required
                    value={resetEmail}
                    onChange={event => setResetEmail(event.target.value)}
                    className="signin-page__input"
                    placeholder="name@organization.com"
                  />
                </div>

                <p className="signin-page__helper-text">
                  Enter the email address associated with your account.
                  We will send a secure link to reset your password.
                </p>

                <button
                  type="submit"
                  className="signin-page__primary-button"
                  disabled={loading}
                >
                  {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
                </button>
                <button
                  type="button"
                  className="signin-page__secondary-button"
                  onClick={exitResetMode}
                  disabled={loading}
                >
                  Back to Login
                </button>
              </form>
            ) : tab === 'login' ? (
              <form onSubmit={handleLoginSubmit}>
                {error && (
                  <div className="signin-page__alert signin-page__alert--danger">
                    {error}
                  </div>
                )}

                <div className="signin-page__input-group">
                  <label
                    htmlFor="signin-email"
                    className="signin-page__input-label"
                  >
                    Organizational Email
                  </label>
                  <input
                    id="signin-email"
                    type="email"
                    name="email"
                    required
                    value={loginForm.email}
                    onChange={event => handleChange(event, setLoginForm)}
                    className="signin-page__input"
                    placeholder="name@organization.com"
                  />
                </div>

                <div className="signin-page__input-group signin-page__input-group--password">
                  <label
                    htmlFor="signin-password"
                    className="signin-page__input-label"
                  >
                    Password
                  </label>
                  <input
                    id="signin-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    required
                    value={loginForm.password}
                    onChange={event => handleChange(event, setLoginForm)}
                    className="signin-page__input signin-page__input--with-icon"
                    placeholder="Enter your secure password"
                  />
                  <button
                    type="button"
                    onClick={handleTogglePasswordVisibility}
                    className="signin-page__password-toggle"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i
                      className={`bi ${
                        showPassword ? 'bi-eye' : 'bi-eye-slash'
                      }`}
                    />
                  </button>
                </div>

                <div className="signin-page__reset-row">
                  <label className="signin-page__remember-label">
                    <input
                      type="checkbox"
                      className="signin-page__checkbox"
                      checked={rememberMe}
                      onChange={event => setRememberMe(event.target.checked)}
                      disabled={loading}
                    />
                    Remember me
                  </label>
                  <button
                    type="button"
                    className="signin-page__text-button"
                    onClick={enterResetMode}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  className="signin-page__primary-button"
                  disabled={loading}
                >
                  {loading ? 'Accessing My Nutrition...' : 'Access My Nutrition'}
                </button>

                <div className="signin-page__divider">
                  <div className="signin-page__divider-line" />
                  <span className="signin-page__divider-text">
                    or continue with
                  </span>
                  <div className="signin-page__divider-line" />
                </div>

                <div className="signin-page__google-button-wrapper">
                  <GoogleButton
                    type="light"
                    label={
                      isGoogleSignInInProgress
                        ? 'Connecting to Workspace...'
                        : 'Sign in with Workspace'
                    }
                    onClick={handleGoogleSignIn}
                    className="signin-page__google-button"
                    style={{
                      opacity: isGoogleButtonDisabled ? 0.6 : 1,
                      pointerEvents: isGoogleButtonDisabled ? 'none' : 'auto'
                    }}
                    disabled={isGoogleButtonDisabled}
                  />
                </div>
              </form>
            ) : (
              <form onSubmit={handleSignUpSubmit}>
                {error && (
                  <div className="signin-page__alert signin-page__alert--danger">
                    {error}
                  </div>
                )}

                <div className="signin-page__input-group">
                  <label
                    htmlFor="signup-email"
                    className="signin-page__input-label"
                  >
                    Organizational Email
                  </label>
                  <input
                    id="signup-email"
                    type="email"
                    name="email"
                    required
                    value={signUpForm.email}
                    onChange={event => handleChange(event, setSignUpForm)}
                    className="signin-page__input"
                    placeholder="name@organization.com"
                  />
                </div>

                <div className="signin-page__input-group">
                  <label
                    htmlFor="signup-password"
                    className="signin-page__input-label"
                  >
                    Create Password
                  </label>
                  <input
                    id="signup-password"
                    type="password"
                    name="password"
                    required
                    value={signUpForm.password}
                    onChange={event => handleChange(event, setSignUpForm)}
                    className="signin-page__input"
                    placeholder="Must be at least 10 characters"
                  />
                </div>

                <div className="signin-page__input-group">
                  <label
                    htmlFor="signup-confirm-password"
                    className="signin-page__input-label"
                  >
                    Confirm Password
                  </label>
                  <input
                    id="signup-confirm-password"
                    type="password"
                    name="confirm"
                    required
                    value={signUpForm.confirm}
                    onChange={event => handleChange(event, setSignUpForm)}
                    className="signin-page__input"
                    placeholder="Re-enter your password"
                  />
                </div>

                <button
                  type="submit"
                  className="signin-page__primary-button signin-page__primary-button--with-margin"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Request Account Access'}
                </button>
              </form>
            )}
          </div>

          <div className="signin-page__secure-footer">
            <div className="signin-page__secure-badge">
              <svg
                className="signin-page__secure-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              HIPAA Compliant & Secure Connection
            </div>

            <div className="signin-page__copyright">
              &copy; 2024 Pure Health Nexus
            </div>
          </div>
        </div>
      </section>

      <AlertModal
        open={alertModalState.open}
        variant={alertModalState.variant}
        preset={alertModalState.preset}
        requester={LOGIN_REQUESTER}
        service={LOGIN_SERVICE}
        onClose={closeAlertModal}
        primaryAction={{
          onClick: closeAlertModal
        }}
      />
    </>
  );
}

export default SignInPage;
